import React, { useState, useEffect } from 'react';
import { AutoComplete, Button, Input, Typography } from 'antd';
import DeloitteLoader from '../../../../ui/DeloitteLoader';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import SearchIcon from '../../../../../assets/icons/search-icon.svg';
import CloseCircle from '../../../../../assets/icons/close-circle.svg';
import { GenAIActions } from '../../../../../libs/store/actions/GenAIActions';
import { IAutoCompleteSearchPayload, ICreateSessionPayload } from '../../../../../libs/models/GenAIModels';
import { IApplicationState, useAppSelector } from '../../../../../libs/store/reducers';
import { STORAGE_CONSTANTS, storage } from '../../../../../libs/services/LocalStorage';

const DEBOUNCE_DELAY = 1000;
interface ISearchBoxProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isDrawer: boolean;
  isKeywordSearch: boolean;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSearchHistoryId?: string;
  setCardContent: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsGlobalSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const SearchBox: React.FC<ISearchBoxProps> = ({
  setShowModal,
  isDrawer,
  isKeywordSearch,
  query,
  setQuery,
  setVisible,
  setCardContent,
  setIsGlobalSearch,
  selectedSearchHistoryId,
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const handleInputChange = (value: string) => {
    setQuery(value);
  };
  const { autoCompleteOptions, loading: loadingAutosuggestions } = useAppSelector(
    ({ genAI: { autoCompleteOptions, loading } }: IApplicationState) => ({
      autoCompleteOptions,
      loading,
    }),
  );
  const userId = storage.getItem(STORAGE_CONSTANTS.userId)!;

  const dispatch = useDispatch();
  const dispatcher = {
    fetchSuggestions: (data: IAutoCompleteSearchPayload) =>
      dispatch(GenAIActions.getAutoCompleteOptionsStart(data)),
    createOrModifySession: (payload: ICreateSessionPayload) =>
      dispatch(GenAIActions.currentChatSessionStart(payload)),
  };

  const handleGenerateClick = () => {
    if (isDrawer) {
      dispatcher.createOrModifySession({
        userId,
        query,
        sessionId: selectedSearchHistoryId,
        isDocSearch: !isKeywordSearch,
      });
    } else {
      setVisible(true);
      setShowModal(false);
      setIsGlobalSearch(true);
    }
    setSuggestions([]);
    setCardContent(query);
    setQuery('');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
  };

  const handleClearQuery = () => {
    setQuery('');
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    autoCompleteOptions && setSuggestions(autoCompleteOptions);
  }, [autoCompleteOptions]);

  // useEffect(() => {
  //   if (query) {
  //     const suggestionsTimeoutId = setTimeout(() => {
  //       dispatcher.fetchSuggestions({
  //         searchText: query,
  //       });
  //     }, DEBOUNCE_DELAY);
  //     return () => clearTimeout(suggestionsTimeoutId);
  //   }
  // }, [query]);

  return (
    <div className="modal-container-search-box">
      <div className="w-100 d-flex">
        {isDrawer && (
          <div className="search-icon-container">
            <img src={SearchIcon} className="search-icon" alt="search" />
          </div>
        )}
        <div className="search">
          <AutoComplete
            value={query}
            options={
              loadingAutosuggestions
                ? []
                : suggestions.map((suggestion) => ({
                    value: suggestion,
                    label: suggestion,
                    key: suggestion,
                  }))
            }
            notFoundContent={
              loadingAutosuggestions ? (
                <div className="flex-center w-100">
                  <DeloitteLoader size={32} style={{ display: 'block', margin: 'auto' }} />
                </div>
              ) : undefined
            }
            dropdownClassName={
              isDrawer ? 'genai-drawer-dropdown-wrapper' : 'genai-modal-dropdown-wrapper'
            }
            onSelect={handleSuggestionClick}
            onChange={handleInputChange}
            dropdownMatchSelectWidth={false}
            onDropdownVisibleChange={handleDropdownVisibleChange}
          >
            <div className="text-side">
              <Input
                placeholder={'Start typing...'}
                value={query}
                className={isDrawer ? 'italic' : 'normal'}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerateClick()}
              />
            </div>
          </AutoComplete>
        </div>
      </div>
      <div className="close-circle-and-generate-button">
        {!isDrawer && (
          <img
            src={CloseCircle}
            className="close-circle-outlined"
            onClick={handleClearQuery}
            alt="Close"
          />
        )}
        <Button
          className="generate-button"
          onClick={handleGenerateClick}
          disabled={isEmpty(isKeywordSearch ? query : selectedSearchHistoryId)}
        >
          <Typography className="generate-button-text">{'Generate'}</Typography>
        </Button>
        <div>
          {!isDrawer && (
            <Button className="close-button" onClick={() => setShowModal(false)}>
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBox;
